import TextField from "@mui/material/TextField";
import { BaseTextFieldProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import { green } from "@mui/material/colors";
import React from "react";
import DiffFee from "work/proposals/redlining/__diff/_views/diff-fee";
import { FeeType } from "work/proposals/redlining/fee-schedule-redline/fee-redline/fee-redline";
import Fee from "_shared/fee-schedules/categories/fees/fee";
import { FeeScheduleCategoryRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-category-redline";
import { ESCAPE_HINT } from "work/proposals/redlining/field-redlines/_views/redline-field-focus";

/** The helper text of a fee the reader cannot commit yet, while it keeps the focus. */
export const FEE_AMOUNT_MISSING_MESSAGE = `Enter an amount, ${ESCAPE_HINT}.`;
export const FEE_UNIT_MISSING_MESSAGE = `Enter a unit or tick Fixed, ${ESCAPE_HINT}.`;

const RedlineTextBox = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "disableFullWidth" && prop !== "cellLayout",
})<{ cellLayout?: boolean; disableFullWidth?: boolean }>(({ cellLayout, disableFullWidth }) => ({
  minWidth: "8rem",
  width: disableFullWidth ? "unset" : "100%",
  "& .MuiTypography-root": {
    height: "unset",
  },
  // A table cell: the field fills whatever width the table gives the column, with no floor of its own
  // (#5405). The value row runs the whole cell.
  ...(cellLayout && {
    margin: 0,
    minWidth: 0,
    width: "100%",
    "& .MuiInputBase-root": {
      alignItems: "center",
      display: "flex",
      minWidth: 0,
      width: "100%",
    },
    // The input is as tall as its one line of fee, so the root centres it. MUI's fixed input height and
    // its 4px-over-5px padding left the text at the top of a taller box, 3px above a flag icon centred
    // on that box (#6628).
    "& .MuiInputBase-input": {
      height: "auto",
      paddingBottom: 0,
      paddingTop: 0,
    },
  }),
}));

export interface FeeRedlineInputProps extends BaseTextFieldProps {
  categoryRedline: FeeScheduleCategoryRedline;
  showTextInput?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  disableFullWidth?: boolean;
  /**
   * Lays the fee out as one cell of a redline table (#5405), the shape every cell of the fee schedule
   * shares: the fee on one line, cut off with an ellipsis where it does not fit.
   */
  cellLayout?: boolean;
  onFeeChange?: (newFee: Fee) => void;
  /**
   * Reports the error the fee's inputs hold while they cannot be committed (no amount, or a rate with
   * no unit), and undefined once they can or Escape has put back the fee. Nothing about that fee has
   * reached `onFeeChange`, so a form must not save while it is set.
   */
  onDraftErrorChange?: (error: string | undefined) => void;
  onShowTextInput?: () => void;
  onHideTextInput?: () => void;
}

export default function FeeRedlineInput(props: Readonly<FeeRedlineInputProps>) {
  const {
    categoryRedline,
    focused,
    readOnly,
    disableFullWidth,
    cellLayout,
    showTextInput: opensForEditing,
    onFeeChange,
    onDraftErrorChange,
    onShowTextInput,
    onHideTextInput,
    ...otherProps
  } = props;

  const variant = props.variant ?? "outlined";
  const hasAcceptedResolvedChange =
    Boolean(categoryRedline.feeRedline?.wasRedlined) &&
    Boolean(categoryRedline.feeRedline?.isResolved) &&
    Boolean(categoryRedline.isAccepted) &&
    !categoryRedline.feeRedline?.wasModified;

  const getEditableFee = React.useCallback((): FeeType | undefined => {
    if (categoryRedline?.feeRedline?.currentEntry !== undefined) return categoryRedline.feeRedline.currentEntry;
    if (categoryRedline?.feeRedline?.revisedEntry !== false) return categoryRedline?.feeRedline?.revisedEntry;
    return categoryRedline?.feeRedline?.originalEntry;
  }, [categoryRedline]);

  const [showTextInput, setShowTextInput] = React.useState<boolean>(
    opensForEditing ?? false
  );
  const [currentFee, setCurrentFee] = React.useState<FeeType | undefined>(
    getEditableFee()
  );
  const [draftError, setDraftErrorState] = React.useState<string>();

  function setDraftError(error: string | undefined) {
    setDraftErrorState(error);
    onDraftErrorChange?.(error);
  }

  // What stops the fee the inputs hold being committed, if anything: a fee with no amount is no fee
  // (#6627), and a rate that is not fixed is a rate per something.
  const currentFeeError = React.useMemo(() => {
    if (!(currentFee instanceof Fee)) return FEE_AMOUNT_MISSING_MESSAGE;
    if (currentFee.unit !== null && currentFee.unit.trim().length === 0) return FEE_UNIT_MISSING_MESSAGE;
    return undefined;
  }, [currentFee]);

  const hasChanged = React.useMemo(() => {
    const editableFee = getEditableFee();
    if (editableFee instanceof Fee && currentFee instanceof Fee) {
      return !editableFee.isEqualTo(currentFee);
    }
    return editableFee !== currentFee;
  }, [currentFee, getEditableFee]);

  // Leaving the fee commits it (#6624). A fee that cannot be committed is refused with its error,
  // and the fee keeps the focus.
  function handleSubmitEdit(): boolean {
    if (currentFeeError || !(currentFee instanceof Fee)) {
      setDraftError(currentFeeError);
      return false;
    }
    setDraftError(undefined);
    setShowTextInput(false);
    if (hasChanged) onFeeChange?.(currentFee);
    onHideTextInput?.();
    return true;
  }

  function handleShowTextInput() {
    // A grid cell is read-only on every row (#5570), and its display takes no focus to open it with.
    if (readOnly) return;
    setCurrentFee(getEditableFee());
    setShowTextInput(true);
    onShowTextInput?.();
  }

  function handleHideTextInput() {
    setCurrentFee(getEditableFee());
    setDraftError(undefined);
    setShowTextInput(false);
    onHideTextInput?.();
  }

  function handleFeeInputChange(fee: Fee | undefined) {
    setCurrentFee(fee);
    setDraftError(undefined);
  }

  return (
    <RedlineTextBox
      {...otherProps}
      cellLayout={cellLayout}
      disableFullWidth={disableFullWidth}
      focused={focused}
      error={otherProps.error || Boolean(draftError)}
      helperText={draftError ?? (hasAcceptedResolvedChange ? "Resolved" : otherProps.helperText)}
      variant={variant}
      margin={props.margin ?? "normal"}
      sx={{
        ...(hasAcceptedResolvedChange && {
          "& .MuiInputLabel-root": {
            color: green[700],
          },
          "& .MuiInputLabel-root.Mui-focused": {
            color: green[700],
          },
          "& .MuiFormHelperText-root": {
            color: green[700],
          },
          "& .MuiInput-root::before": {
            borderBottomColor: green[500],
          },
          "& .MuiInput-root:hover:not(.Mui-disabled, .Mui-error)::before": {
            borderBottomColor: green[600],
          },
          "& .MuiInput-root::after": {
            borderBottomColor: green[700],
          },
        }),
      }}
      slotProps={{
        htmlInput: {
          isNewlyAdded: categoryRedline.currentEntry && !categoryRedline.revisedEntry,
          feeRedline: categoryRedline.feeRedline,
          acceptedResolved: hasAcceptedResolvedChange,
          acceptedResolvedRemoval: hasAcceptedResolvedChange && categoryRedline.isRemoved,
          acceptedRemovedLabel:
            categoryRedline.feeRedline?.revisedEntry?.toString() ??
            categoryRedline.feeRedline?.originalEntry?.toString() ??
            "",
          showTextInput: showTextInput,
          singleLine: cellLayout,
          editable: !readOnly,
          fieldLabel: typeof otherProps.label === "string" ? otherProps.label : "Fee",
          onFeeInputChange: handleFeeInputChange,
          onDisplayFocus: handleShowTextInput,
          onTextInputSubmit: handleSubmitEdit,
          onTextInputCancel: handleHideTextInput,
        },
        input: {
          inputComponent: DiffFee,
          // Only an underlined variant has an underline to take away. An outlined field — the form layout
          // the category panel draws (#5570) — would pass the prop through to the DOM.
          ...(variant !== "outlined" && { disableUnderline: true }),
        },
        inputLabel: {
          shrink: categoryRedline.feeRedline !== undefined
        }
      }}
    />
  );
}
